// src/components/NewVersionBanner.jsx
// Slim top banner that appears when a newer build has been uploaded to the
// server. Checks every 5 minutes and whenever the tab regains focus; never
// reloads on its own — the user clicks Refresh. Fails silent on any error.
import { useEffect, useState } from "react";
import { RefreshCw } from "lucide-react";
import {
  mainJsFromManifest,
  runningMainJs,
  isNewVersion,
} from "../utils/versionCheck";

const CHECK_EVERY_MS = 5 * 60 * 1000;

export default function NewVersionBanner() {
  const [show, setShow] = useState(false);

  useEffect(() => {
    let stopped = false;
    const check = async () => {
      try {
        const res = await fetch("/asset-manifest.json", { cache: "no-store" });
        if (!res.ok) return;
        const manifest = await res.json();
        if (stopped) return;
        if (isNewVersion(runningMainJs(document), mainJsFromManifest(manifest))) {
          setShow(true);
        }
      } catch {
        /* dev server or offline — no manifest, no banner */
      }
    };
    const onVisible = () => {
      if (document.visibilityState === "visible") check();
    };
    const timer = setInterval(check, CHECK_EVERY_MS);
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      stopped = true;
      clearInterval(timer);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, []);

  if (!show) return null;
  return (
    // Deliberately NOT a brand colour. Navy is the app's own furniture, so a
    // navy bar reads as part of the page and gets scrolled past. This lime
    // belongs to nothing else in the product, which is the whole point: when
    // the screen needs refreshing it is ALWAYS this colour, and nothing else
    // ever is. Keep it that way — reusing it elsewhere spends the signal.
    // top-[var(--tes-safe-top)]: just below the iPhone's status bar in the installed app (src/index.css);
    // 0 in a browser. pl-16 on a phone keeps the words clear of the floating menu button.
    <div className="fixed top-[var(--tes-safe-top)] inset-x-0 z-[100] pointer-events-none bg-[#CCFF00] text-[#1E293B] text-sm font-medium flex items-center justify-center gap-3 py-2 pl-16 pr-4 lg:px-4 print:hidden">
      <span>New Version Available</span>
      <button
        onClick={() => window.location.reload()}
        className="pointer-events-auto inline-flex items-center gap-1.5 bg-[#1E293B] hover:opacity-90 text-white font-semibold rounded-md px-3 py-1 text-xs"
      >
        <RefreshCw size={12} /> Refresh
      </button>
    </div>
  );
}
